import React, {useCallback, useContext} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {PermissionsTab} from 'src/admin-rights-service/modules/permissions';
import {UserLink} from 'src/components/links';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ROUTES} from 'src/constants';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {FormSection} from '@pb/kit-component-form-section';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {StickyElement} from '@pb/kit-component-sticky-element';
import {LocaleContext} from '@pb/kit-context-locale';


import {ProfileTabs} from '../../components/tabs';
import {useGetProfileHistoryRecord} from '../hooks/useGetProfileHistoryRecord';

import {ProfileHistoryHeadline} from './profile-history-headline';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {ProfileChangeRecord} from 'src/openapi/admin/models/ProfileChangeRecord';

type Props = {
    profileId: number,
    profileHistory: ProfileChangeRecord[],
};

export const ProfileHistoryRecord = ({profileId, profileHistory}: Props) => {
    const navigate = useNavigate();
    const queryParams = useParams<{recordId: string}>();
    const recordId = queryParams.recordId ? +queryParams.recordId : -1;
    const {locale} = useContext(LocaleContext);

    const {
        isLoading,
        loadingError,
        data,
    } = useGetProfileHistoryRecord({profileId, recordId});

    const setActiveTab = useCallback((tab: string, newRecordId?: number) => {
        if (newRecordId) {
            navigate(`${ROUTES.PROFILE}/${profileId}/${tab}/${newRecordId}`);
        } else {
            navigate(`${ROUTES.PROFILE}/${profileId}/${tab}`);
        }
    }, [profileId, navigate]);

    return (
        <ServerResponseHandler
            serverError={loadingError}
            accessRight={ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE as AccessRight}>
            <>
                <div>
                    <StickyElement>
                        <ProfileHistoryHeadline
                            profileHistory={profileHistory}
                            isLoading={isLoading}
                            profileId={profileId}
                            profileName={data.profileName}
                        />
                        <ProfileTabs activeTab="history" onTabChange={setActiveTab}/>
                    </StickyElement>
                    <PermissionsTab
                        isLoading={isLoading}
                        disabled={true}
                        profile={{
                            usersCounts: 0,
                            isPrimary: false,
                            profileId: data.profileId,
                            profileName: data.profileName,
                            profileDescription: data.profileDescription,
                            rightsCodes: data.rightsCodes,
                        }}
                        onSetDescription={() => undefined}
                        onSetPermissions={() => undefined}
                        profileHistory={(
                            <FormSection headline="Change Details">
                                <Group direction="v" alignItems="start">
                                    <Item>
                                        <Label>Change type</Label>
                                        {data.operationDescription}
                                    </Item>
                                    <Item>
                                        <Label>Changed by</Label>
                                        <UserLink
                                            userId={data.editor.userId}
                                            caption={[
                                                data.editor.firstName,
                                                data.editor.lastName,
                                            ].join(' ') || data.editor.email}
                                            place="Profile History Record Page"
                                        />
                                    </Item>
                                    <Item>
                                        <Label>Change date</Label>
                                        {FormattedDate.format({
                                            value: data.eventTime,
                                            format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE_TIME'),
                                        })}
                                    </Item>
                                    <Item>
                                        <Label>Notes</Label>
                                        {data.notes}
                                    </Item>
                                </Group>
                            </FormSection>
                        )}
                    />
                </div>
            </>
        </ServerResponseHandler>
    );
};
